import { AuthUIData, AuthUIState, StateData } from "@/types/auth-ui";
import { Auth } from "aws-amplify";
import { computed, ref } from "vue";

const _authUIData = ref<AuthUIData>({
  state: AuthUIState.SignIn,
});

const authUIData = computed(() => _authUIData.value.data);
const authUIState = computed(() => _authUIData.value.state);
export function useGammaAuthenticator() {
  function setUiState(state: AuthUIState, data?: StateData) {
    _authUIData.value = {
      state,
      data: data,
    };
  }

  async function signIn(email: string, password: string) {
    if (!(email && password)) return;

    const result = await Auth.signIn(email.trim(), password);

    if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
      setUiState(AuthUIState.ForceNewPassword, {
        forceNewPasswordUser: result,
      });
    } else {
      setUiState(AuthUIState.SignedIn);
    }
  }

  async function signOut() {
    await Auth.signOut();
    setUiState(AuthUIState.SignIn);
  }

  async function submitNewPassword(password: string) {
    if (
      authUIState.value !== AuthUIState.ForceNewPassword ||
      !_authUIData.value.data?.forceNewPasswordUser
    ) {
      throw new Error("Session expired. Please refresh the page.");
    }

    await Auth.completeNewPassword(
      _authUIData.value.data.forceNewPasswordUser,
      password.trim()
    );
    setUiState(AuthUIState.SignedIn);
  }

  async function startResetPassword(email: string) {
    await Auth.forgotPassword(email);
    setUiState(AuthUIState.ResetPasswordConfirm, { email: email });
  }

  async function confirmResetPassword(code: string, password: string) {
    if (
      authUIState.value !== AuthUIState.ResetPasswordConfirm ||
      !_authUIData.value.data?.email
    ) {
      throw new Error("Invalid state to use confirm reset password");
    }

    await Auth.forgotPasswordSubmit(
      _authUIData.value.data?.email.trim(),
      code.trim(),
      password.trim()
    );

    setUiState(AuthUIState.SignIn);
  }

  async function resendCode() {
    if (
      authUIState.value !== AuthUIState.ResetPasswordConfirm ||
      !_authUIData.value.data?.email
    ) {
      throw new Error("Invalid state to use confirm reset password");
    }
    await Auth.forgotPassword(_authUIData.value.data?.email.trim());
  }

  return {
    authUIState,
    authUIData,
    setUiState,
    signIn,
    signOut,
    submitNewPassword,
    startResetPassword,
    confirmResetPassword,
    resendCode,
  };
}
